/**
 * Standalone Affiliate Landing Page
 * 
 * A full-screen marketing page for potential affiliates.
 * No header/footer - designed to be shared privately with interested parties.
 * 
 * Route: /become-affiliate
 * 
 * Flow:
 * - If user is already an active affiliate → redirect to /affiliate
 * - Otherwise → show landing page with application form
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { useAffiliateData } from "./hooks/use-affiliate-data";
import { AffiliateApplicationForm } from "./components/affiliate-application-form";
import { AffiliatePendingStatus } from "./components/affiliate-pending-status";
import { Spinner } from "@/components/spinner";

export const BecomeAffiliatePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isReady } = useUnifiedAuth();
  const { affiliate, isLoading } = useAffiliateData();

  // Redirect active affiliates to dashboard
  useEffect(() => {
    if (isReady && isAuthenticated && !isLoading && affiliate?.status === "active") {
      navigate("/affiliate", { replace: true });
    }
  }, [isReady, isAuthenticated, isLoading, affiliate, navigate]);

  // Show loading while auth is initializing
  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner />
      </div>
    );
  }

  // If authenticated, wait for affiliate data to load before deciding what to show
  if (isAuthenticated && isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner />
      </div>
    );
  }

  // If user has a pending application, show pending status
  if (isAuthenticated && affiliate?.status === "pending") {
    return (
      <div className="min-h-screen bg-background">
        <AffiliatePendingStatus affiliate={affiliate} />
      </div>
    );
  }

  // Show the landing page with application form
  // This handles: not authenticated, authenticated but not affiliate, or rejected affiliate
  return <AffiliateApplicationForm />;
};

